import "bootstrap/dist/css/bootstrap.min.css";
import { onValue, push, ref, update } from "firebase/database";
import { useEffect, useState } from "react";
import { db } from "../Firebase";
import TableFilters, { useTableFilters } from "./TableFilters";

const Masters = () => {
  /* ================= DATA ================= */
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [panchayats, setPanchayats] = useState([]);
  const [grams, setGrams] = useState([]);
  const [urbanAreas, setUrbanAreas] = useState([]);

  /* ================= FORM ================= */
  const [type, setType] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [block, setBlock] = useState("");
  const [panchayatName, setPanchayatName] = useState("");
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);

  /* ================= MODAL ================= */
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("states");

  /* ================= FILTERS ================= */
  const stateColumns = [{ key: 'name', label: 'State Name' }];
  const cityColumns = [{ key: 'state', label: 'State' }, { key: 'name', label: 'City Name' }];
  const blockColumns = [{ key: 'state', label: 'State' }, { key: 'city', label: 'City' }, { key: 'name', label: 'Block Name' }];
  const panchayatColumns = [{ key: 'state', label: 'State' }, { key: 'city', label: 'City' }, { key: 'block', label: 'Block' }, { key: 'name', label: 'Panchayat Name' }];
  const gramColumns = [{ key: 'state', label: 'State' }, { key: 'city', label: 'City' }, { key: 'block', label: 'Block' }, { key: 'panchayatName', label: 'Panchayat' }, { key: 'name', label: 'Gram Name' }];
  const urbanColumns = [{ key: 'state', label: 'State' }, { key: 'city', label: 'City' }, { key: 'name', label: 'Urban Area Name' }];

  const stateFilters = useTableFilters(states, stateColumns);
  const cityFilters = useTableFilters(cities, cityColumns);
  const blockFilters = useTableFilters(blocks, blockColumns);
  const panchayatFilters = useTableFilters(panchayats, panchayatColumns);
  const gramFilters = useTableFilters(grams, gramColumns);
  const urbanFilters = useTableFilters(urbanAreas, urbanColumns);

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchData = (path, setter) =>
      onValue(ref(db, path), snap =>
        setter(
          snap.exists()
            ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v }))
            : []
        )
      );

    fetchData("masters/states", setStates);
    fetchData("masters/cities", setCities);
    fetchData("masters/blocks", setBlocks);
    fetchData("masters/panchayats", setPanchayats);
    fetchData("masters/grams", setGrams);
    fetchData("masters/urbanAreas", setUrbanAreas);
  }, []);

  /* ================= HELPERS ================= */
  const exists = (list, condition) =>
    list.some(item =>
      Object.keys(condition).every(k => item[k] === condition[k])
    );

  const resetForm = () => {
    setEditId(null);
    setState("");
    setCity("");
    setBlock("");
    setPanchayatName("");
    setName("");
  };

  const openModal = (t, row = null) => {
    setType(t);
    resetForm();

    if (row) {
      setEditId(row.id);
      setState(row.state || "");
      setCity(row.city || "");
      setBlock(row.block || "");
      setPanchayatName(row.panchayatName || "");
      setName(row.name || "");
    }
    setShowModal(true);
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id, type, itemName) => {
    if (!window.confirm(`क्या आप वाकई "${itemName}" को हटाना चाहते हैं?`)) return;
    
    try {
      const { remove } = await import('firebase/database');
      
      // Delete the main item using remove instead of update
      await remove(ref(db, `masters/${type}s/${id}`));
      
      // Cascade delete related items
      if (type === 'state') {
        const relatedCities = cities.filter(c => c.state === itemName);
        const relatedBlocks = blocks.filter(b => b.state === itemName);
        const relatedPanchayats = panchayats.filter(p => p.state === itemName);
        const relatedGrams = grams.filter(g => g.state === itemName);
        const relatedUrbanAreas = urbanAreas.filter(u => u.state === itemName);
        
        for (const city of relatedCities) await remove(ref(db, `masters/cities/${city.id}`));
        for (const block of relatedBlocks) await remove(ref(db, `masters/blocks/${block.id}`));
        for (const panchayat of relatedPanchayats) await remove(ref(db, `masters/panchayats/${panchayat.id}`));
        for (const gram of relatedGrams) await remove(ref(db, `masters/grams/${gram.id}`));
        for (const urbanArea of relatedUrbanAreas) await remove(ref(db, `masters/urbanAreas/${urbanArea.id}`));
      }
      
      if (type === 'city') {
        const relatedBlocks = blocks.filter(b => b.city === itemName);
        const relatedPanchayats = panchayats.filter(p => p.city === itemName);
        const relatedGrams = grams.filter(g => g.city === itemName);
        const relatedUrbanAreas = urbanAreas.filter(u => u.city === itemName);
        
        for (const block of relatedBlocks) await remove(ref(db, `masters/blocks/${block.id}`));
        for (const panchayat of relatedPanchayats) await remove(ref(db, `masters/panchayats/${panchayat.id}`));
        for (const gram of relatedGrams) await remove(ref(db, `masters/grams/${gram.id}`));
        for (const urbanArea of relatedUrbanAreas) await remove(ref(db, `masters/urbanAreas/${urbanArea.id}`));
      }
      
      if (type === 'block') {
        const relatedPanchayats = panchayats.filter(p => p.block === itemName);
        const relatedGrams = grams.filter(g => g.block === itemName);
        
        for (const panchayat of relatedPanchayats) await remove(ref(db, `masters/panchayats/${panchayat.id}`));
        for (const gram of relatedGrams) await remove(ref(db, `masters/grams/${gram.id}`));
      }
      
      if (type === 'panchayat') {
        const relatedGrams = grams.filter(g => g.panchayatName === itemName);
        for (const gram of relatedGrams) await remove(ref(db, `masters/grams/${gram.id}`));
      }
      
      alert('आइटम और संबंधित डेटा सफलतापूर्वक हटा दिया गया ✅');
    } catch (err) {
      console.error(err);
      alert('डिलीट फेल ❌: ' + err.message);
    }
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    if (!name.trim()) return alert("Name required");

    try {
      if (type === "state") {
        if (!editId && exists(states, { name }))
          return alert("Duplicate state");

        editId
          ? await update(ref(db, `masters/states/${editId}`), { name })
          : await push(ref(db, "masters/states"), { name });
      }

      if (type === "city") {
        if (!state) return alert("Select state");
        if (!editId && exists(cities, { state, name }))
          return alert("Duplicate city");

        const data = { state, name };
        editId
          ? await update(ref(db, `masters/cities/${editId}`), data)
          : await push(ref(db, "masters/cities"), data);
      }

      if (type === "block") {
        if (!state || !city) return alert("Select state & city");
        if (!editId && exists(blocks, { city, name }))
          return alert("Duplicate block");

        const data = { state, city, name };
        editId
          ? await update(ref(db, `masters/blocks/${editId}`), data)
          : await push(ref(db, "masters/blocks"), data);
      }

      if (type === "panchayat") {
        if (!state || !city || !block) return alert("Select state, city & block");
        if (!editId && exists(panchayats, { block, name }))
          return alert("Duplicate panchayat");

        const data = { state, city, block, name };
        editId
          ? await update(ref(db, `masters/panchayats/${editId}`), data)
          : await push(ref(db, "masters/panchayats"), data);
      }

      if (type === "gram") {
        if (!state || !city || !block || !panchayatName)
          return alert("Select panchayat");

        if (!editId && exists(grams, { block, panchayatName, name }))
          return alert("Duplicate gram");

        const data = { state, city, block, panchayatName, name };
        editId
          ? await update(ref(db, `masters/grams/${editId}`), data)
          : await push(ref(db, "masters/grams"), data);
      }

      if (type === "urban") {
        if (!state || !city) return alert("Select state & city");
        if (!editId && exists(urbanAreas, { city, name }))
          return alert("Duplicate urban area");

        const data = { state, city, name };
        editId
          ? await update(ref(db, `masters/urbanAreas/${editId}`), data)
          : await push(ref(db, "masters/urbanAreas"), data);
      }

      resetForm();
      setShowModal(false);
    } catch (err) {
      console.error(err);
      alert("Save failed ❌");
    }
  };


  /* ================= UI ================= */
  return (
    <div className="container mt-4">
      <h4>📍 Location Masters</h4>

      {/* Tabs for navigation */}
      <ul className="nav nav-tabs mb-4" role="tablist">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "states" ? "active" : ""}`}
            onClick={() => setActiveTab("states")}
          >
            States ({states.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "cities" ? "active" : ""}`}
            onClick={() => setActiveTab("cities")}
          >
            Cities ({cities.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "blocks" ? "active" : ""}`}
            onClick={() => setActiveTab("blocks")}
          >
            Blocks ({blocks.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "panchayats" ? "active" : ""}`}
            onClick={() => setActiveTab("panchayats")}
          >
            Panchayats ({panchayats.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "grams" ? "active" : ""}`}
            onClick={() => setActiveTab("grams")}
          >
            Grams ({grams.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "urban" ? "active" : ""}`}
            onClick={() => setActiveTab("urban")}
          >
            Urban Areas ({urbanAreas.length})
          </button>
        </li>
      </ul>

      {/* States Table - Read Only */}
      {activeTab === "states" && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>States <span className="badge bg-info ms-2">Total: {stateFilters.filteredData.length}/{states.length}</span></h5>
            <button
              className="btn btn-sm btn-success"
              onClick={() => openModal("state")}
            >
              + Add State
            </button>
          </div>
          <TableFilters
            searchTerm={stateFilters.searchTerm}
            setSearchTerm={stateFilters.setSearchTerm}
            sortField={stateFilters.sortField}
            setSortField={stateFilters.setSortField}
            sortOrder={stateFilters.sortOrder}
            setSortOrder={stateFilters.setSortOrder}
            columns={stateColumns}
            placeholder="Search states..."
          />
          <table className="table table-bordered table-striped table-sm">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "40px", fontSize: "0.75rem" }}>S.No</th>
                <th style={{ fontSize: "0.75rem" }}>State Name</th>
                <th style={{ width: "100px", fontSize: "0.75rem" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stateFilters.filteredData.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center text-muted">
                    No records found
                  </td>
                </tr>
              ) : (
                stateFilters.filteredData.map((row, idx) => (
                  <tr key={row.id}>
                    <td className="text-center" style={{ fontSize: "0.8rem" }}>{idx + 1}</td>
                    <td style={{ fontSize: "0.8rem" }}>{row.name || "-"}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-xs btn-warning px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => openModal("state", row)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-xs btn-danger px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => handleDelete(row.id, "state", row.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Cities Table - Read Only */}
      {activeTab === "cities" && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Cities <span className="badge bg-info ms-2">Total: {cityFilters.filteredData.length}/{cities.length}</span></h5>
            <button
              className="btn btn-sm btn-success"
              onClick={() => openModal("city")}
            >
              + Add City
            </button>
          </div>
          <TableFilters
            searchTerm={cityFilters.searchTerm}
            setSearchTerm={cityFilters.setSearchTerm}
            sortField={cityFilters.sortField}
            setSortField={cityFilters.setSortField}
            sortOrder={cityFilters.sortOrder}
            setSortOrder={cityFilters.setSortOrder}
            columns={cityColumns}
            placeholder="Search cities..."
          />
          <table className="table table-bordered table-striped table-sm">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "40px", fontSize: "0.75rem" }}>S.No</th>
                <th style={{ fontSize: "0.75rem" }}>State</th>
                <th style={{ fontSize: "0.75rem" }}>City Name</th>
                <th style={{ width: "100px", fontSize: "0.75rem" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cityFilters.filteredData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted">
                    No records found
                  </td>
                </tr>
              ) : (
                cityFilters.filteredData.map((row, idx) => (
                  <tr key={row.id}>
                    <td className="text-center" style={{ fontSize: "0.8rem" }}>{idx + 1}</td>
                    <td style={{ fontSize: "0.8rem" }}>{row.state || "-"}</td>
                    <td style={{ fontSize: "0.8rem" }}>{row.name || "-"}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-xs btn-warning px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => openModal("city", row)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-xs btn-danger px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => handleDelete(row.id, "city", row.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Blocks Table */}
      {activeTab === "blocks" && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Blocks <span className="badge bg-info ms-2">Total: {blockFilters.filteredData.length}/{blocks.length}</span></h5>
            <button
              className="btn btn-sm btn-success"
              onClick={() => openModal("block")}
            >
              + Add Block
            </button>
          </div>
          <TableFilters
            searchTerm={blockFilters.searchTerm}
            setSearchTerm={blockFilters.setSearchTerm}
            sortField={blockFilters.sortField}
            setSortField={blockFilters.setSortField}
            sortOrder={blockFilters.sortOrder}
            setSortOrder={blockFilters.setSortOrder}
            columns={blockColumns}
            placeholder="Search blocks..."
          />
          <table className="table table-bordered table-striped table-sm">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "40px", fontSize: "0.75rem" }}>S.No</th>
                <th style={{ fontSize: "0.75rem" }}>State</th>
                <th style={{ fontSize: "0.75rem" }}>City</th>
                <th style={{ fontSize: "0.75rem" }}>Block Name</th>
                <th style={{ width: "100px", fontSize: "0.75rem" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blockFilters.filteredData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No records found
                  </td>
                </tr>
              ) : (
                blockFilters.filteredData.map((row, idx) => (
                  <tr key={row.id}>
                    <td className="text-center" style={{ fontSize: "0.8rem" }}>{idx + 1}</td>
                    <td style={{ fontSize: "0.8rem" }}>{row.state || "-"}</td>
                    <td style={{ fontSize: "0.8rem" }}>{row.city || "-"}</td>
                    <td style={{ fontSize: "0.8rem" }}>{row.name || "-"}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-xs btn-warning px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => openModal("block", row)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-xs btn-danger px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => handleDelete(row.id, "block", row.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Panchayats Table - Updated */}
      {activeTab === "panchayats" && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Panchayats <span className="badge bg-info ms-2">Total: {panchayatFilters.filteredData.length}/{panchayats.length}</span></h5>
            <button
              className="btn btn-sm btn-success"
              onClick={() => openModal("panchayat")}
            >
              + Add Panchayat
            </button>
          </div>
          <TableFilters
            searchTerm={panchayatFilters.searchTerm}
            setSearchTerm={panchayatFilters.setSearchTerm}
            sortField={panchayatFilters.sortField}
            setSortField={panchayatFilters.setSortField}
            sortOrder={panchayatFilters.sortOrder}
            setSortOrder={panchayatFilters.setSortOrder}
            columns={panchayatColumns}
            placeholder="Search panchayats..."
          />
          <table className="table table-bordered table-striped table-sm">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "50px" }}>S.No</th>
                <th>State</th>
                <th>City</th>
                <th>Block</th>
                <th>Panchayat Name</th>
                <th style={{ width: "120px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {panchayatFilters.filteredData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No records found
                  </td>
                </tr>
              ) : (
                panchayatFilters.filteredData.map((row, idx) => (
                  <tr key={row.id}>
                    <td className="text-center">{idx + 1}</td>
                    <td>{row.state || "-"}</td>
                    <td>{row.city || "-"}</td>
                    <td>{row.block || "-"}</td>
                    <td>{row.name || "-"}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-xs btn-warning px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => openModal("panchayat", row)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-xs btn-danger px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => handleDelete(row.id, "panchayat", row.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Grams Table - Read Only */}
      {activeTab === "grams" && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Grams <span className="badge bg-info ms-2">Total: {gramFilters.filteredData.length}/{grams.length}</span></h5>
            <button
              className="btn btn-sm btn-success"
              onClick={() => openModal("gram")}
            >
              + Add Gram
            </button>
          </div>
          <TableFilters
            searchTerm={gramFilters.searchTerm}
            setSearchTerm={gramFilters.setSearchTerm}
            sortField={gramFilters.sortField}
            setSortField={gramFilters.setSortField}
            sortOrder={gramFilters.sortOrder}
            setSortOrder={gramFilters.setSortOrder}
            columns={gramColumns}
            placeholder="Search grams..."
          />
          <table className="table table-bordered table-striped table-sm">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "50px" }}>S.No</th>
                <th>State</th>
                <th>City</th>
                <th>Block</th>
                <th>Panchayat</th>
                <th>Gram Name</th>
                <th style={{ width: "120px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {gramFilters.filteredData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    No records found
                  </td>
                </tr>
              ) : (
                gramFilters.filteredData.map((row, idx) => (
                  <tr key={row.id}>
                    <td className="text-center">{idx + 1}</td>
                    <td>{row.state || "-"}</td>
                    <td>{row.city || "-"}</td>
                    <td>{row.block || "-"}</td>
                    <td>{row.panchayatName || "-"}</td>
                    <td>{row.name || "-"}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-xs btn-warning px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => openModal("gram", row)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-xs btn-danger px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => handleDelete(row.id, "gram", row.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Urban Areas Table - Read Only */}
      {activeTab === "urban" && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Urban Areas <span className="badge bg-info ms-2">Total: {urbanFilters.filteredData.length}/{urbanAreas.length}</span></h5>
            <button
              className="btn btn-sm btn-success"
              onClick={() => openModal("urban")}
            >
              + Add Urban Area
            </button>
          </div>
          <TableFilters
            searchTerm={urbanFilters.searchTerm}
            setSearchTerm={urbanFilters.setSearchTerm}
            sortField={urbanFilters.sortField}
            setSortField={urbanFilters.setSortField}
            sortOrder={urbanFilters.sortOrder}
            setSortOrder={urbanFilters.setSortOrder}
            columns={urbanColumns}
            placeholder="Search urban areas..."
          />
          <table className="table table-bordered table-striped table-sm">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "50px" }}>S.No</th>
                <th>State</th>
                <th>City</th>
                <th>Urban Area Name</th>
                <th style={{ width: "120px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {urbanFilters.filteredData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No records found
                  </td>
                </tr>
              ) : (
                urbanFilters.filteredData.map((row, idx) => (
                  <tr key={row.id}>
                    <td className="text-center">{idx + 1}</td>
                    <td>{row.state || "-"}</td>
                    <td>{row.city || "-"}</td>
                    <td>{row.name || "-"}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-xs btn-warning px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => openModal("urban", row)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-xs btn-danger px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                          onClick={() => handleDelete(row.id, "urbanArea", row.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => setShowModal(false)} />
          <div className="modal show d-block" style={{ zIndex: 1050, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', overflow: 'auto' }} tabIndex="-1">
            <div className="modal-dialog" style={{ margin: '1.75rem auto', maxWidth: '500px' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editId ? "Edit" : "Add"} {type}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>

                <div className="modal-body">
                  {type !== "state" && (
                    <Select label="State" value={state} set={setState} options={states} />
                  )}
                  {type !== "state" && type !== "city" && (
                    <Select
                      label="City"
                      value={city}
                      set={setCity}
                      options={cities.filter(c => c.state === state)}
                    />
                  )}
                  {type !== "state" && type !== "city" && type !== "block" && (
                    <Select
                      label="Block"
                      value={block}
                      set={setBlock}
                      options={blocks.filter(b => b.city === city)}
                    />
                  )}
                  {type === "gram" && (
                    <Select
                      label="Panchayat"
                      value={panchayatName}
                      set={setPanchayatName}
                      options={panchayats.filter(p => p.block === block)}
                    />
                  )}
                  <Input label="Name" value={name} set={setName} />
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleSave}>
                    {editId ? "Update" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ================= REUSABLE ================= */
const Select = ({ label, value, set, options }) => (
  <>
    <label className="form-label">{label}</label>
    <select
      className="form-select mb-2"
      value={value}
      onChange={e => set(e.target.value)}
    >
      <option value="">Select</option>
      {options.map(o => (
        <option key={o.id} value={o.name}>
          {o.name}
        </option>
      ))}
    </select>
  </>
);

const Input = ({ label, value, set }) => (
  <>
    <label className="form-label">{label}</label>
    <input
      className="form-control mb-2"
      value={value}
      onChange={e => set(e.target.value)}
    />
  </>
);

export default Masters;
